import { useCallback, useReducer, useRef } from 'react';
// eslint-disable-next-line no-restricted-imports -- v5 form stack, migrate to v7 (DX-194)
import { Schema, ValidationError } from 'yup-legacy';
import { FormErrors, GoalFormApi, GoalFormState, SaveStatus } from './types';

type Action<T> =
  | { type: 'SET_FIELD'; name: keyof T; value: T[keyof T] }
  | { type: 'SET_VALUES'; partial: Partial<T> }
  | { type: 'RESET'; values: T }
  | { type: 'SET_ERRORS'; errors: FormErrors<T> }
  | { type: 'SET_FIELD_ERROR'; name: keyof T; error: string | undefined }
  | { type: 'SET_SUBMITTING'; value: boolean }
  | { type: 'INCREMENT_SUBMIT_COUNT' }
  | { type: 'SET_SAVE_STATUS'; name: keyof T; status: SaveStatus }
  | { type: 'CLEAR_SAVE_STATUSES' };

function createReducer<T extends Record<string, unknown>>() {
  return (state: GoalFormState<T>, action: Action<T>): GoalFormState<T> => {
    switch (action.type) {
      case 'SET_FIELD': {
        const newErrors = { ...state.errors };
        delete newErrors[action.name];
        return {
          ...state,
          values: { ...state.values, [action.name]: action.value },
          errors: newErrors,
        };
      }
      case 'SET_VALUES':
        return {
          ...state,
          values: { ...state.values, ...action.partial },
        };
      case 'RESET':
        return {
          ...state,
          values: action.values,
          initialValues: action.values,
          errors: {},
          isSubmitting: false,
          saveStatuses: {},
        };
      case 'SET_ERRORS':
        return { ...state, errors: action.errors };
      case 'SET_FIELD_ERROR': {
        const errors = { ...state.errors };
        if (action.error === undefined) {
          delete errors[action.name];
        } else {
          errors[action.name] = action.error;
        }
        return { ...state, errors };
      }
      case 'SET_SUBMITTING':
        return { ...state, isSubmitting: action.value };
      case 'INCREMENT_SUBMIT_COUNT':
        return { ...state, submitCount: state.submitCount + 1 };
      case 'SET_SAVE_STATUS':
        return {
          ...state,
          saveStatuses: {
            ...state.saveStatuses,
            [action.name]: action.status,
          },
        };
      case 'CLEAR_SAVE_STATUSES':
        return { ...state, saveStatuses: {} };
    }
  };
}

export function useGoalFormState<T extends Record<string, unknown>>(
  initialValues: T,
  validationSchema: Schema<unknown>,
): GoalFormApi<T> {
  const initialValuesRef = useRef(initialValues);

  const [state, dispatch] = useReducer(createReducer<T>(), {
    values: initialValues,
    errors: {},
    initialValues,
    isSubmitting: false,
    submitCount: 0,
    saveStatuses: {},
  });

  const schemaRef = useRef(validationSchema);
  schemaRef.current = validationSchema;

  const setFieldValue = useCallback(
    <K extends keyof T>(name: K, value: T[K]) => {
      dispatch({ type: 'SET_FIELD', name, value });
    },
    [],
  );

  const setValues = useCallback((partial: Partial<T>) => {
    dispatch({ type: 'SET_VALUES', partial });
  }, []);

  const resetForm = useCallback((newValues?: T) => {
    const vals = newValues ?? initialValuesRef.current;
    if (newValues) {
      initialValuesRef.current = newValues;
    }
    dispatch({ type: 'RESET', values: vals });
  }, []);

  const validate = useCallback((): FormErrors<T> | null => {
    try {
      schemaRef.current.validateSync(state.values, { abortEarly: false });
      dispatch({ type: 'SET_ERRORS', errors: {} });
      return null;
    } catch (err) {
      if (err instanceof ValidationError) {
        const errors: FormErrors<T> = {};
        for (const inner of err.inner) {
          if (inner.path && !errors[inner.path as keyof T]) {
            errors[inner.path as keyof T] = inner.message;
          }
        }
        dispatch({ type: 'SET_ERRORS', errors });
        return errors;
      }
      throw err;
    }
  }, [state.values]);

  const validateField = useCallback(
    (name: keyof T): string | null => {
      try {
        schemaRef.current.validateSyncAt(String(name), state.values);
        dispatch({ type: 'SET_FIELD_ERROR', name, error: undefined });
        return null;
      } catch (err) {
        if (err instanceof ValidationError) {
          const message = err.message;
          dispatch({ type: 'SET_FIELD_ERROR', name, error: message });
          return message;
        }
        // Yup throws a plain Error when the path is not in the schema.
        // No rule defined for this field means nothing to validate — treat as valid.
        if (
          err instanceof Error &&
          err.message.startsWith('The schema does not contain the path:')
        ) {
          dispatch({ type: 'SET_FIELD_ERROR', name, error: undefined });
          return null;
        }
        throw err;
      }
    },
    [state.values],
  );

  const isDirty = useCallback(
    (name?: keyof T): boolean => {
      if (name) {
        return state.values[name] !== state.initialValues[name];
      }
      return Object.keys(state.values).some(
        (key) => state.values[key] !== state.initialValues[key],
      );
    },
    [state.values, state.initialValues],
  );

  const setIsSubmitting = useCallback((value: boolean) => {
    dispatch({ type: 'SET_SUBMITTING', value });
    if (value) {
      dispatch({ type: 'INCREMENT_SUBMIT_COUNT' });
    }
  }, []);

  const setFieldError = useCallback(
    (name: keyof T, error: string | undefined) => {
      dispatch({ type: 'SET_FIELD_ERROR', name, error });
    },
    [],
  );

  const setSaveStatus = useCallback((name: keyof T, status: SaveStatus) => {
    dispatch({ type: 'SET_SAVE_STATUS', name, status });
  }, []);

  const clearSaveStatuses = useCallback(() => {
    dispatch({ type: 'CLEAR_SAVE_STATUSES' });
  }, []);

  return {
    ...state,
    setFieldValue,
    setValues,
    resetForm,
    validate,
    validateField,
    isDirty,
    setIsSubmitting,
    setFieldError,
    setSaveStatus,
    clearSaveStatuses,
  };
}
